import type { ReactNode } from 'react';
import { Badge } from './ui/badge';
import { Label } from './ui/label';

/* ── page furniture ──────────────────────────────────────────────────────── */

export function PageHeader({
  title,
  description,
  actions,
}: {
  title: string;
  description?: string;
  actions?: ReactNode;
}) {
  return (
    <div className="mb-5 flex flex-wrap items-start justify-between gap-4">
      <div className="min-w-0">
        <h1 className="text-[19px] font-semibold tracking-[-0.015em]">{title}</h1>
        {description && <p className="mt-1 max-w-[68ch] text-[13px] text-ink-2">{description}</p>}
      </div>
      {actions && <div className="flex flex-wrap gap-2">{actions}</div>}
    </div>
  );
}

/** A titled block that says what it is for — the operator's unit of comprehension. */
export function Panel({
  title,
  description,
  actions,
  children,
}: {
  /** Omitted inside a tab, where the tab label already names the surface. */
  title?: string;
  description?: string;
  actions?: ReactNode;
  children: ReactNode;
}) {
  return (
    <section className="mb-5 overflow-hidden rounded-[7px] border border-rule bg-panel shadow-[var(--shadow-panel)]">
      <header className="flex flex-wrap items-start justify-between gap-3 border-b border-rule px-4 py-2.5">
        <div className="min-w-0">
          {title && <h2 className="text-[13px] font-semibold">{title}</h2>}
          {description && (
            <p className={`max-w-[85ch] text-[12px] text-ink-2 ${title ? 'mt-0.5' : ''}`}>{description}</p>
          )}
        </div>
        {actions && <div className="flex flex-wrap items-center gap-2">{actions}</div>}
      </header>
      {/* Tables bleed to the panel edge with a negative margin, so the padding
          here is what their first and last cells re-inset against. */}
      <div className="px-4 py-3.5">{children}</div>
    </section>
  );
}

export function EmptyState({ title, hint, action }: { title: string; hint?: string; action?: ReactNode }) {
  return (
    <div className="flex flex-col items-start gap-3 rounded-[5px] border border-dashed border-rule bg-raised/60 px-4 py-6">
      <div>
        <p className="text-[13px] font-medium">{title}</p>
        {hint && <p className="mt-1 max-w-[70ch] text-[12px] text-ink-2">{hint}</p>}
      </div>
      {action}
    </div>
  );
}

export function ErrorNote({ error }: { error: unknown }) {
  if (!error) return null;
  const message = error instanceof Error ? error.message : String(error);
  return (
    <p
      role="alert"
      className="mt-2 rounded-[5px] border border-bad/30 bg-bad-soft px-2.5 py-1.5 text-[12px] text-bad"
    >
      {message}
    </p>
  );
}

/**
 * One field: label, control, hint. Every settings form on the console is built
 * from these, so a cell with a hint can never push its neighbour's label out of
 * line — `content-start` pins every row to the top of the grid row it shares.
 */
export function Field({
  label,
  hint,
  htmlFor,
  className = '',
  children,
}: {
  label: string;
  hint?: ReactNode;
  htmlFor?: string;
  className?: string;
  children: ReactNode;
}) {
  return (
    <div className={`grid content-start gap-1.5 ${className}`}>
      <Label htmlFor={htmlFor}>{label}</Label>
      {children}
      {hint && <p className="text-[12px] text-ink-2">{hint}</p>}
    </div>
  );
}

/**
 * A settings form. Capped at a readable width on purpose: a password input as
 * wide as a 27-inch display is a worse target, not a more generous one, and the
 * eye loses the line between a label and the box it belongs to.
 */
export function FormGrid({
  columns = 2,
  className = '',
  children,
  ...props
}: { columns?: 1 | 2 | 3; className?: string; children: ReactNode } & React.FormHTMLAttributes<HTMLFormElement>) {
  const grid = { 1: '', 2: 'sm:grid-cols-2', 3: 'sm:grid-cols-2 lg:grid-cols-3' }[columns];
  return (
    <form {...props} className={`grid max-w-[60rem] items-start gap-x-4 gap-y-3 ${grid} ${className}`}>
      {children}
    </form>
  );
}

/* ── state ───────────────────────────────────────────────────────────────── */

export type RunState = 'running' | 'stopping' | 'queued' | 'done' | 'failed';

const STATE: Record<
  RunState,
  { label: string; badge: 'run' | 'warn' | 'secondary' | 'ok' | 'destructive'; dot: string }
> = {
  running: { label: 'Running', badge: 'run', dot: 'bg-run' },
  stopping: { label: 'Stopping', badge: 'warn', dot: 'bg-warn' },
  queued: { label: 'Queued', badge: 'secondary', dot: 'bg-ink-3' },
  done: { label: 'Done', badge: 'ok', dot: 'bg-ok' },
  failed: { label: 'Failed', badge: 'destructive', dot: 'bg-bad' },
};

/** One status vocabulary for the whole console: same dot, same word, everywhere. */
export function StatusDot({ state }: { state: RunState }) {
  const live = state === 'running' || state === 'stopping';
  return (
    <span
      aria-hidden
      className={`inline-block size-[7px] shrink-0 rounded-full ${STATE[state].dot} ${live ? 'live-dot' : ''}`}
    />
  );
}

export function StatusBadge({ state, children }: { state: RunState; children?: ReactNode }) {
  const { label, badge } = STATE[state];
  return (
    <Badge variant={badge} className="gap-1.5">
      <StatusDot state={state} />
      {children ?? label}
    </Badge>
  );
}

/** A run in flight, drawn as work moving rather than a spinner sitting still. */
export function LiveBar({ label }: { label?: string }) {
  return (
    <div className="live-bar h-[3px] w-full rounded-full" role="presentation" aria-label={label}>
      <span className="live-bar-scan" />
    </div>
  );
}

/* ── data ────────────────────────────────────────────────────────────────── */

export function Transcript({ text }: { text: string }) {
  return (
    <pre className="mt-2 max-h-96 overflow-auto rounded-[5px] border border-rule bg-field px-3 py-2.5 font-mono text-[11.5px] leading-relaxed whitespace-pre-wrap text-ink-2">
      {text || 'Nothing written yet.'}
    </pre>
  );
}

export const money = (value: number | null | undefined): string => `$${(value ?? 0).toFixed(2)}`;

/**
 * A run billed to a Claude subscription costs nothing per run — the figure the
 * CLI reports is what the same work would have cost at API list price. Printing
 * it as plain money invites someone to add up a bill that does not exist.
 */
export const cost = (value: number | null | undefined, billing: string | null | undefined): string =>
  billing === 'api' ? money(value) : `≈${money(value)}`;

/**
 * Whether any of these runs cost money. A subscription install charges nothing
 * per run, so a column of ≈$0.00 is a column of noise — and worse, one that
 * invites somebody to add it up.
 */
export const anyMetered = (rows: { billing?: string | null }[]): boolean =>
  rows.some((row) => row.billing === 'api');

export const billingLabel = (billing: string | null | undefined): string =>
  billing === 'subscription' ? 'subscription' : billing === 'api' ? 'API' : 'unknown';

export const costTitle = (billing: string | null | undefined): string =>
  billing === 'subscription'
    ? 'What this run would have cost at API list price. It was billed to a Claude subscription, so nothing was charged for it.'
    : billing === 'api'
      ? 'Charged to the API credential this worker runs with.'
      : 'taskpilot could not see which credential the agent used.';

export function since(iso: string): string {
  const minutes = Math.round((Date.now() - Date.parse(iso)) / 60000);
  return minutes < 60 ? `${minutes}m` : `${Math.floor(minutes / 60)}h ${minutes % 60}m`;
}

export function when(iso: string | null): string {
  return iso
    ? new Date(iso).toLocaleString([], { month: '2-digit', day: '2-digit', hour: '2-digit', minute: '2-digit' })
    : '';
}
